import { forwardRef, useImperativeHandle } from "react";
import { TextInput, TagsInput } from "@mantine/core";
import { EmailEditor } from "../EmailEditor";
import { useEmailEditor } from "../../hooks/useEmailEditor";

export type DraftBodyEditorRef = { getHTML: () => string };

const DraftBodyEditor = forwardRef<DraftBodyEditorRef, { initialHtml: string }>(
  function DraftBodyEditor({ initialHtml }, ref) {
    const editor = useEmailEditor(initialHtml);
    useImperativeHandle(
      ref,
      () => ({
        getHTML: () => editor?.getHTML() ?? "",
      }),
      [editor],
    );
    if (!editor) return null;
    return <EmailEditor editor={editor} />;
  },
);

type DraftEditorSectionProps = {
  draftUid: string;
  initialHtml: string;
  subject: string;
  to: string[];
  onSubjectChange: (v: string) => void;
  onToChange: (v: string[]) => void;
  onSave: () => void;
  isSaving: boolean;
};

export const DraftEditorSection = forwardRef<
  DraftBodyEditorRef,
  DraftEditorSectionProps
>(function DraftEditorSection(
  {
    draftUid,
    initialHtml,
    subject,
    to,
    onSubjectChange,
    onToChange,
    onSave,
    isSaving,
  },
  ref,
) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-slate-700">Edit draft</h3>
      <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Subject
          </label>
          <TextInput
            value={subject}
            onChange={(e) => onSubjectChange(e.currentTarget.value)}
            size="sm"
            radius="md"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">To</label>
          <TagsInput value={to} onChange={onToChange} size="sm" radius="md" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Body</label>
          <div className="min-h-[200px] rounded-lg border border-slate-200 bg-white">
            <DraftBodyEditor key={draftUid} ref={ref} initialHtml={initialHtml} />
          </div>
        </div>
        <button
          type="button"
          disabled={isSaving}
          onClick={onSave}
          className="rounded-lg bg-[#4B7F73] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#3F6C61] disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save draft"}
        </button>
      </div>
    </div>
  );
});
